import { isEqual } from 'lodash-es'

import { BookingDTOCargoFCL, ContainerItemDTO } from '@/clients/booking'

import { FormBookingDTOCargoFCL, FormContainerItemDTO } from './types'

const isValidQuantity = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0

export const normalizeQuantity = (value: unknown): number => (isValidQuantity(value) ? Math.floor(value) : 0)

const hasAnyWeight = (grossWeights: (number | null)[]): boolean => grossWeights.some((w) => w != null)

const canMergeFragments = (last: FormContainerItemDTO, next: FormContainerItemDTO): boolean =>
  Array.isArray(last.grossWeights) &&
  Array.isArray(next.grossWeights) &&
  last.type === next.type &&
  last.containerNumber === next.containerNumber &&
  isEqual(last.tags, next.tags)

export const hasContainerGrossWeights = (containers: FormContainerItemDTO[] | undefined): boolean =>
  (containers ?? []).some(
    (c) => c.grossWeightInKg != null || (Array.isArray(c.grossWeights) && c.grossWeights.some((w) => w != null))
  )

/**
 * Maps BE-shaped containers into form rows. A single weighted container
 * (quantity 1) becomes a per-container fragment so adjacent same-shape
 * fragments (produced by the submit split) collapse back into one row with a
 * `grossWeights` array. A quantity>1 row with a single weight stays a single
 * inline row total. Adjacency matters so we never re-order what the BE sent.
 */
export const consolidateContainersForForm = (containers: ContainerItemDTO[] | undefined): FormContainerItemDTO[] => {
  if (containers == null || containers.length === 0) return []

  const merged: FormContainerItemDTO[] = []
  for (const entry of containers) {
    const quantity = normalizeQuantity(entry.quantity)
    const { grossWeightInKg, ...rest } = entry

    if (grossWeightInKg == null) {
      merged.push({ ...rest, quantity })
      continue
    }

    if (quantity === 1) {
      const fragment: FormContainerItemDTO = { ...rest, quantity: 1, grossWeights: [grossWeightInKg] }
      const last = merged[merged.length - 1]
      if (last != null && canMergeFragments(last, fragment)) {
        last.quantity = (last.quantity ?? 0) + 1
        last.grossWeights = [...(last.grossWeights ?? []), grossWeightInKg]
        continue
      }
      merged.push(fragment)
      continue
    }

    merged.push({ ...rest, quantity, grossWeightInKg })
  }

  return merged
}

/**
 * Flattens form rows back into BE DTO entries, per row. A per-container row
 * (carrying a `grossWeights` array) splits into `quantity` single-unit entries
 * with their own `grossWeightInKg`; a fully-empty one stays a single weightless
 * row so the container count is preserved. A single inline row keeps its
 * `grossWeightInKg` as the row total. Weightless rows pass through untouched.
 */
export const flattenContainersForSubmit = (containers: FormContainerItemDTO[] | undefined): ContainerItemDTO[] => {
  if (containers == null || containers.length === 0) return []

  const flattened: ContainerItemDTO[] = []
  for (const row of containers) {
    const { grossWeights, grossWeightInKg, ...rest } = row

    if (Array.isArray(grossWeights)) {
      const quantity = normalizeQuantity(rest.quantity)
      const weights = grossWeights.slice(0, quantity)
      if (!hasAnyWeight(weights)) {
        flattened.push({ ...rest } as ContainerItemDTO)
        continue
      }
      for (let i = 0; i < quantity; i++) {
        flattened.push({ ...rest, quantity: 1, grossWeightInKg: weights[i] ?? null } as ContainerItemDTO)
      }
      continue
    }

    if (grossWeightInKg != null) {
      flattened.push({ ...rest, grossWeightInKg } as ContainerItemDTO)
      continue
    }

    flattened.push({ ...rest } as ContainerItemDTO)
  }
  return flattened
}

export const flattenCargoFCLForSubmit = (cargoFCL: FormBookingDTOCargoFCL): BookingDTOCargoFCL => ({
  ...cargoFCL,
  containers: flattenContainersForSubmit(cargoFCL.containers),
})
